import { useState } from "react";
import {
    Modal,
    ModalBody,
    ModalFooter,
    Table,
    Button,
} from "reactstrap";
import { DebounceInput } from "react-debounce-input";
import { api } from "../helpers";
import Alert from "react-s-alert-v3";
import _ from "lodash";
import { Loading } from "../components";

export default function RTOProTaxZoneLookupModal({ isOpen, onClose, onSelect, contractTaxRate }) {
    const [searchText, setSearchText] = useState("");
    const [results, setResults] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false);
    const [pendingZone, setPendingZone] = useState(null);

    function search(value) {
        setSearchText(value);
        if (!value || value.length < 2) {
            setResults([]);
            setHasSearched(false);
            return;
        }
        setIsLoading(true);
        api.post("reference/RTOProTaxZoneList", { searchText: value, page: 1, pageSize: 20 })
            .then(response => {
                setResults(response.data.data.dataSource);
                setHasSearched(true);
            })
            .catch(() => Alert.error("There was an error searching tax zones."))
            .finally(() => setIsLoading(false));
    }

    function selectZone(zone) {
        const ratesMatch =
            contractTaxRate == null ||
            zone.taxRate == null ||
            Math.abs(Number(zone.taxRate) - Number(contractTaxRate)) < 0.00001;
        if (ratesMatch) {
            onSelect(zone, false);
            handleClose();
        } else {
            setPendingZone(zone);
        }
    }

    function confirmSelection(updateContractRate) {
        onSelect(pendingZone, updateContractRate);
        handleClose();
    }

    function handleClose() {
        setSearchText("");
        setResults([]);
        setHasSearched(false);
        setPendingZone(null);
        onClose();
    }

    return (
        <Modal isOpen={isOpen} toggle={handleClose} size="lg">
            <div className="modal-header">
                <h5 className="modal-title">Find a RTO Pro Tax Zone</h5>
            </div>
            <ModalBody>
                {pendingZone ? (
                    <p>
                        The selected tax zone (<strong>{pendingZone.taxZone}</strong>) has a rate of{" "}
                        <strong>{pendingZone.taxRate}</strong>, which does not match this contract's current
                        tax rate of <strong>{contractTaxRate}</strong>. 
                    </p>
                ) : (
                    <>
                        <p className="text-muted" style={{fontSize: "13px"}}>
                            Type the tax zone code or any jurisdiction words &mdash; e.g. &ldquo;TX-5249546&rdquo;
                            or &ldquo;TX WISE WISE CO ESD 1&rdquo;.
                        </p>
                        <DebounceInput
                            className="form-control"
                            placeholder="Search tax zone or jurisdiction..."
                            minLength={2}
                            debounceTimeout={300}
                            value={searchText}
                            onChange={(event) => search(event.target.value)}
                        />
                        <div className="mt-3">
                            {isLoading
                                ? <Loading/>
                                : (
                                    <div style={{overflowX: "auto"}}>
                                        <Table size="sm" bordered striped>
                                            <thead>
                                            <tr>
                                                <th>Tax Zone</th>
                                                <th>State</th>
                                                <th>County</th>
                                                <th>City</th>
                                                <th>District 1</th>
                                                <th>District 2</th>
                                                <th>District 3</th>
                                                <th>Tax Rate</th>
                                                <th/>
                                            </tr>
                                            </thead>
                                            <tbody>
                                            {results.length
                                                ? _.map(results, z => (
                                                    <tr key={`lookup-zone-${z.id}`}>
                                                        <td>{z.taxZone}</td>
                                                        <td>{z.state}</td>
                                                        <td>{z.county}</td>
                                                        <td>{z.city}</td>
                                                        <td>{z.district1}</td>
                                                        <td>{z.district2}</td>
                                                        <td>{z.district3}</td>
                                                        <td>{z.taxRate}</td>
                                                        <td>
                                                            <Button
                                                                size="sm"
                                                                className="btn-success"
                                                                style={{color: "white"}}
                                                                onClick={() => selectZone(z)}
                                                            >
                                                                Select
                                                            </Button>
                                                        </td>
                                                    </tr>
                                                ))
                                                : (
                                                    <tr>
                                                        <td colSpan="9" className="text-center text-muted">
                                                            {hasSearched ? "No matching tax zones found." : "Start typing to search."}
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                        </div>
                    </>
                )}
            </ModalBody>
            <ModalFooter>
                {pendingZone ? (
                    <>
                        <Button color="secondary" onClick={() => setPendingZone(null)}>
                            Cancel
                        </Button>
                        <Button color="success" className="text-light" onClick={() => confirmSelection(false)}>
                            Confirm Selection
                        </Button>
                    </>
                ) : (
                    <Button color="secondary" onClick={handleClose}>Close</Button>
                )}
            </ModalFooter>
        </Modal>
    );
}
